import React from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, ArrowUpRight, Download, Linkedin, Mail } from "lucide-react";
import { PROFILE } from "./data";
import ResumeModal from "../resume/ResumeModal";
import { resolveFileUrl } from "./fileUrl";

const RotatingTitles = () => {
  const [idx, setIdx] = React.useState(0);
  React.useEffect(() => {
    const t = setInterval(() => setIdx((i) => (i + 1) % PROFILE.titles.length), 2600);
    return () => clearInterval(t);
  }, []);
  return (
    <div className="relative h-[44px] md:h-[58px] overflow-hidden inline-block min-w-[280px] md:min-w-[420px]">
      {PROFILE.titles.map((t, i) => (
        <motion.span
          key={t}
          className="absolute left-0 right-0 text-[#e6c769] font-serif italic"
          style={{ fontFamily: "'Cormorant Garamond', serif" }}
          initial={false}
          animate={{
            y: i === idx ? 0 : i < idx ? -60 : 60,
            opacity: i === idx ? 1 : 0,
          }}
          transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
        >
          {t}
        </motion.span>
      ))}
    </div>
  );
};

const Hero = () => {
  const [resumeOpen, setResumeOpen] = React.useState(false);
  const ref = React.useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);
  const yShift = useTransform(scrollYProgress, [0, 1], [0, 180]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.08]);

  const [tilt, setTilt] = React.useState({ x: 0, y: 0 });
  const onMove = (e) => {
    const cx = window.innerWidth / 2;
    const cy = window.innerHeight / 2;
    setTilt({ x: (e.clientX - cx) / cx, y: (e.clientY - cy) / cy });
  };

  return (
    <section
      id="hero"
      ref={ref}
      onMouseMove={onMove}
      className="relative min-h-screen flex items-center pt-28 pb-20 px-6 md:px-10 overflow-hidden"
    >
      {/* glow orbs */}
      <motion.div
        style={{ x: tilt.x * 40, y: tilt.y * 40 }}
        className="pointer-events-none absolute -top-32 -left-20 w-[480px] h-[480px] rounded-full"
      >
        <div className="w-full h-full rounded-full bg-[#3b82f6]/20 blur-[120px]" />
      </motion.div>
      <motion.div
        style={{ x: tilt.x * -40, y: tilt.y * -40 }}
        className="pointer-events-none absolute -bottom-40 -right-20 w-[560px] h-[560px] rounded-full"
      >
        <div className="w-full h-full rounded-full bg-[#d4af37]/15 blur-[140px]" />
      </motion.div>

      <motion.div style={{ opacity, y: yShift, scale }} className="max-w-[1400px] mx-auto w-full grid lg:grid-cols-[1.4fr_1fr] gap-16 items-center relative z-10">
        <div>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.8 }}
            className="inline-flex items-center gap-3 px-4 py-2 rounded-full bg-white/[0.04] backdrop-blur-xl border border-white/10"
          >
            <span className="w-2 h-2 rounded-full bg-[#e6c769] animate-pulse" />
            <span className="text-[11px] tracking-[0.3em] uppercase text-white/70">A personal archive · 2025</span>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.35, duration: 1, ease: [0.16, 1, 0.3, 1] }}
            className="mt-8 text-[58px] md:text-[96px] lg:text-[120px] leading-[0.92] tracking-[-0.03em] text-white font-extrabold"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            Utkarsh
            <br />
            <span className="italic font-medium text-white/90">Bokadia</span>
            <span className="inline-block w-3 h-3 bg-[#e6c769] rounded-full ml-3 align-baseline" />
          </motion.h1>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6, duration: 0.8 }}
            className="mt-6 text-[22px] md:text-[34px] leading-tight text-white/70"
            style={{ fontFamily: "'Cormorant Garamond', serif" }}
          >
            <RotatingTitles />
          </motion.div>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8, duration: 0.8 }}
            className="mt-8 max-w-xl text-[15px] md:text-[17px] leading-[1.7] text-white/55"
          >
            {PROFILE.shortBio}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1, duration: 0.8 }}
            className="mt-10 flex flex-wrap items-center gap-3"
          >
            <button
              onClick={() => document.getElementById("story")?.scrollIntoView({ behavior: "smooth" })}
              className="group inline-flex items-center gap-3 px-6 py-3.5 rounded-full bg-[#e6c769] text-black font-medium text-[14px] tracking-wide hover:bg-white transition-colors"
            >
              Explore My Journey
              <ArrowDown className="w-4 h-4 group-hover:translate-y-0.5 transition-transform" />
            </button>
            <button
              type="button"
              onClick={() => setResumeOpen(true)}
              data-testid="hero-resume-btn"
              className="inline-flex items-center gap-2 px-5 py-3.5 rounded-full border border-white/15 text-white/80 hover:bg-white/[0.06] hover:border-white/30 transition-colors text-[14px]"
            >
              <Download className="w-4 h-4" /> Resume
            </button>
            <a
              href={PROFILE.linkedin}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 px-5 py-3.5 rounded-full border border-white/15 text-white/80 hover:bg-white/[0.06] hover:border-white/30 transition-colors text-[14px]"
            >
              <Linkedin className="w-4 h-4" /> LinkedIn
            </a>
            <a
              href={`mailto:${PROFILE.email}`}
              className="inline-flex items-center gap-2 px-5 py-3.5 rounded-full border border-white/15 text-white/80 hover:bg-white/[0.06] hover:border-white/30 transition-colors text-[14px]"
            >
              <Mail className="w-4 h-4" /> Contact
            </a>
          </motion.div>
        </div>

        {/* Portrait card */}
        <motion.div
          initial={{ opacity: 0, x: 40, y: 20 }}
          animate={{ opacity: 1, x: 0, y: 0 }}
          transition={{ delay: 0.4, duration: 1.1, ease: [0.16, 1, 0.3, 1] }}
          style={{ transform: `perspective(1200px) rotateX(${tilt.y * -6}deg) rotateY(${tilt.x * 8}deg)` }}
          className="relative aspect-[3/4] max-w-[440px] mx-auto w-full"
        >
          <div className="absolute inset-0 rounded-[28px] bg-gradient-to-br from-[#e6c769]/30 via-transparent to-[#3b82f6]/30 blur-2xl" />
          <div className="relative h-full rounded-[28px] overflow-hidden border border-white/10 bg-gradient-to-br from-white/[0.06] to-white/[0.02] backdrop-blur-2xl">
            {PROFILE.portrait ? (
              <img src={resolveFileUrl(PROFILE.portrait)} alt={PROFILE.name} className="w-full h-full object-cover" style={{ objectPosition: "center top" }} />
            ) : (
              <div className="absolute inset-0 grid place-items-center">
                <div className="text-center px-6">
                  <div className="w-32 h-32 mx-auto rounded-full bg-gradient-to-br from-[#e6c769] to-[#b6862e] grid place-items-center text-black text-3xl font-bold mb-6">UB</div>
                  <div className="text-[11px] tracking-[0.3em] uppercase text-white/40">Portrait Reserved</div>
                  <div className="text-white/70 text-sm mt-2 max-w-[260px] mx-auto">Upload your professional portrait — it lives here.</div>
                </div>
              </div>
            )}
            {/* Corner labels */}
            <div className="absolute top-5 left-5 right-5 flex items-center justify-between text-[10px] tracking-[0.3em] uppercase text-white/60">
              <span>UB · Portrait</span>
              <span>2025</span>
            </div>
            <div className="absolute bottom-5 left-5 right-5 flex items-center justify-between text-[10px] tracking-[0.3em] uppercase text-white/60">
              <span>Ahmedabad · IND</span>
              <span className="inline-flex items-center gap-1"><ArrowUpRight className="w-3 h-3" /> Archive</span>
            </div>
          </div>
        </motion.div>
      </motion.div>

      {/* scroll cue */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6, duration: 1 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2"
      >
        <div className="text-[10px] tracking-[0.3em] uppercase text-white/40">Scroll</div>
        <motion.div
          animate={{ y: [0, 8, 0] }}
          transition={{ duration: 1.8, repeat: Infinity }}
          className="w-px h-10 bg-gradient-to-b from-white/40 to-transparent"
        />
      </motion.div>

      <ResumeModal open={resumeOpen} onClose={() => setResumeOpen(false)} />
    </section>
  );
};

export default Hero;
